import type { Category } from '@/types/category';

// Resolves the `?parent=<id>` query-param the CategoryDetailPage's
// "Add Subcategory" button appends when redirecting back to /categories.
// Returns the action the page should take so the URL never silently lingers.
export type ParentParamAction =
  | { kind: 'noop' }
  | { kind: 'open-create-with-parent'; parentId: string }
  | { kind: 'clear-param' };

export function resolveParentParam(
  parentParam: string | null,
  categories: Category[],
  canEdit: boolean,
): ParentParamAction {
  if (!parentParam) return { kind: 'noop' };
  if (!canEdit) return { kind: 'noop' };
  if (categories.length === 0) return { kind: 'noop' };
  const parent = categories.find((c) => c.id === parentParam);
  if (!parent) return { kind: 'clear-param' };
  return { kind: 'open-create-with-parent', parentId: parent.id };
}

// Parent picker for an existing category: every category in the tree, labelled
// with its full path ("Snacks / Chips"), minus the category itself and its own
// subtree (a category cannot move under its own descendant).
export function buildParentOptions(
  categories: Category[],
  categoryId: string | undefined,
): { value: string; label: string }[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const pathOf = (c: Category): Category[] => {
    const chain: Category[] = [];
    const seen = new Set<string>();
    for (
      let cur: Category | undefined = c;
      cur && !seen.has(cur.id);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined
    ) {
      seen.add(cur.id);
      chain.unshift(cur);
    }
    return chain;
  };
  return categories
    .map((c) => ({ c, chain: pathOf(c) }))
    .filter(({ chain }) => !categoryId || !chain.some((p) => p.id === categoryId))
    .map(({ c, chain }) => ({ value: c.id, label: chain.map((p) => p.name).join(' / ') }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
